import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import CareLineLayout from "../components/careline/CareLineLayout";
import DoctorScheduleModal from "../components/careline/DoctorScheduleModal";
import { dokterService, tenantApi, clinicService } from "../services/api";

// ─── Icons ────────────────────────────────────────────────────────────────────
const ArrowIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-4 h-4"
    >
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
    </svg>
);

const CalendarIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-4 h-4 text-cyan-500"
    >
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);

// ─── Skeleton — Featured Card ─────────────────────────────────────────────────
const SkeletonFeatured = () => (
    <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_4px_24px_rgba(15,23,42,0.07)] flex flex-col items-center pointer-events-none">
        <div className="w-24 h-24 rounded-full bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:600px_100%] animate-[shimmer_1.4s_infinite_linear] mb-6" />
        <div className="h-4 rounded-md w-2/3 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:600px_100%] animate-[shimmer_1.4s_infinite_linear] mb-2" />
        <div className="h-3 rounded-md w-1/3 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:600px_100%] animate-[shimmer_1.4s_infinite_linear] mb-6" />
        <div className="h-px w-full bg-slate-100 mb-6" />
        <div className="h-3 rounded-md w-1/2 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:600px_100%] animate-[shimmer_1.4s_infinite_linear] mb-8" />
        <div className="h-12 rounded-2xl w-full bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:600px_100%] animate-[shimmer_1.4s_infinite_linear]" />
    </div>
);

// ─── Skeleton — List Row ──────────────────────────────────────────────────────
const SkeletonRow = () => (
    <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_24px_rgba(15,23,42,0.07)] flex items-center justify-between pointer-events-none">
        <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:600px_100%] animate-[shimmer_1.4s_infinite_linear]" />
            <div>
                <div className="h-3.5 rounded-md w-36 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:600px_100%] animate-[shimmer_1.4s_infinite_linear] mb-2" />
                <div className="h-3 rounded-md w-24 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:600px_100%] animate-[shimmer_1.4s_infinite_linear]" />
            </div>
        </div>
        <div className="h-9 w-28 rounded-xl bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:600px_100%] animate-[shimmer_1.4s_infinite_linear]" />
    </div>
);

// ─── Featured Doctor Card ─────────────────────────────────────────────────────
const FeaturedCard = ({
    doc,
    index,
    onSchedule,
    getImageUrl,
    getSpecialization,
    getPoliName,
}) => (
    <article
        className="group relative bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_4px_24px_rgba(15,23,42,0.07),0_1px_4px_rgba(15,23,42,0.04)] flex flex-col items-center text-center overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] hover:shadow-[0_12px_40px_rgba(6,182,212,0.18),0_4px_12px_rgba(15,23,42,0.08)] hover:-translate-y-1 hover:border-cyan-100 animate-[cardIn_0.5s_ease_both]"
        style={{ animationDelay: `${index * 100}ms` }}
    >
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-cyan-400 to-teal-500 scale-x-0 group-hover:scale-x-100 transition-transform duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] origin-left" />

        <div className="relative mb-6">
            <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-md bg-slate-50">
                <img
                    src={getImageUrl(doc.foto)}
                    alt={doc.nama_dokter}
                    className="w-full h-full object-cover"
                />
            </div>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-sm">
                <div className="w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-white" />
            </div>
        </div>

        <h3 className="font-[Plus_Jakarta_Sans] font-bold text-slate-900 text-lg leading-tight mb-1">
            {doc.nama_dokter}
        </h3>
        <p className="font-[DM_Sans] text-[11px] font-bold text-cyan-500 uppercase tracking-wider mb-6">
            {getSpecialization(getPoliName(doc.poli))}
        </p>

        <div className="w-full h-px bg-slate-100 mb-6" />

        <div className="w-full flex flex-col items-start gap-3 mb-8">
            <span className="font-[DM_Sans] text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                {getPoliName(doc.poli)}
            </span>
            <div className="w-full flex items-center justify-between">
                <div className="flex items-center gap-2 text-slate-700">
                    <CalendarIcon />
                    <span className="font-[DM_Sans] text-sm font-bold">
                        {doc.jadwal || "Belum ada jadwal"}
                    </span>
                </div>
                <div className="inline-flex items-center gap-1.5 bg-cyan-50 px-3 py-1 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 inline-block" />
                    <span className="font-[DM_Sans] text-[10px] font-bold text-cyan-600 uppercase tracking-wide">
                        Tersedia
                    </span>
                </div>
            </div>
        </div>

        <button
            onClick={() => onSchedule(doc)}
            className="w-full py-3.5 bg-gradient-to-r from-cyan-500 to-teal-500 text-white font-[Plus_Jakarta_Sans] font-bold rounded-2xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/20 active:scale-[0.98] hover:shadow-cyan-500/30 hover:brightness-105"
        >
            Lihat Jadwal
            <ArrowIcon />
        </button>
    </article>
);

// ─── Doctor List Row ──────────────────────────────────────────────────────────
const DoctorRow = ({
    doc,
    index,
    onSchedule,
    getImageUrl,
    getSpecialization,
    getPoliName,
}) => (
    <div
        className="group bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_24px_rgba(15,23,42,0.07),0_1px_4px_rgba(15,23,42,0.04)] flex items-center justify-between transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] hover:shadow-[0_12px_40px_rgba(6,182,212,0.12)] hover:-translate-y-0.5 hover:border-cyan-100 animate-[cardIn_0.5s_ease_both]"
        style={{ animationDelay: `${index * 60}ms` }}
    >
        <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-slate-100 bg-slate-50 flex-shrink-0">
                <img
                    src={getImageUrl(doc.foto)}
                    alt={doc.nama_dokter}
                    className="w-full h-full object-cover"
                />
            </div>
            <div>
                <h3 className="font-[Plus_Jakarta_Sans] font-bold text-slate-900 leading-tight">
                    {doc.nama_dokter}
                </h3>
                <p className="font-[DM_Sans] text-sm text-slate-400 mt-0.5">
                    {getSpecialization(getPoliName(doc.poli))}
                </p>
            </div>
        </div>
        <button
            onClick={() => onSchedule(doc)}
            className="flex-shrink-0 flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-teal-500 text-white font-[Plus_Jakarta_Sans] text-xs font-bold rounded-xl transition-all duration-200 shadow-md shadow-cyan-500/10 hover:brightness-105 active:scale-[0.98]"
        >
            Lihat Jadwal
            <ArrowIcon />
        </button>
    </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const Doctors = () => {
    const { slug } = useParams();
    const isTenant = Boolean(slug);
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [doctors, setDoctors] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [clinicBrand, setClinicBrand] = useState(null);
    const [tenantError, setTenantError] = useState(false);

    const API_BASE_URL =
        process.env.REACT_APP_API_BASE_URL?.replace("/api", "") ||
        "http://localhost:8000";

    const handleOpenSchedule = (doctor) => {
        setSelectedDoctor(doctor);
        setIsScheduleModalOpen(true);
    };

    const handleCloseSchedule = () => {
        setIsScheduleModalOpen(false);
    };

    useEffect(() => {
        if (!isTenant) {
            setClinicBrand(null);
            setTenantError(false);
            return;
        }
        let cancelled = false;
        clinicService
            .getBySlug(slug)
            .then((res) => {
                if (cancelled) return;
                if (res.data?.success) setClinicBrand(res.data.data);
                else setTenantError(true);
            })
            .catch(() => {
                if (!cancelled) setTenantError(true);
            });
        return () => {
            cancelled = true;
        };
    }, [isTenant, slug]);

    useEffect(() => {
        const fetchDoctors = async () => {
            try {
                setIsLoading(true);
                const response = isTenant
                    ? await tenantApi(slug).dokter.getAll()
                    : await dokterService.getAll();
                if (response.data?.success) {
                    setDoctors(response.data.data || []);
                }
            } catch (error) {
                console.error("Error fetching doctors:", error);
                if (isTenant) setTenantError(true);
            } finally {
                setIsLoading(false);
            }
        };
        fetchDoctors();
    }, [isTenant, slug]);

    const activeDoctors = doctors.filter(
        (doc) => !doc.status || doc.status === "aktif",
    );
    const featuredDoctors = activeDoctors.slice(0, 3);
    const allDoctors = activeDoctors;

    const getImageUrl = (path) => {
        if (!path) return "https://i.pravatar.cc/150?u=placeholder";
        if (path.startsWith("http")) return path;
        return `${API_BASE_URL}${path}`;
    };

    const getPoliName = (poli) => poli?.nama_poli || "Umum";

    const getSpecialization = (poliName) => {
        if (!poliName) return "Dokter Umum";
        if (poliName.toLowerCase().includes("gigi")) return "Dokter Gigi";
        if (poliName.toLowerCase().includes("anak")) return "Dokter Anak";
        if (poliName.toLowerCase().includes("syaraf")) return "Dokter Saraf";
        if (poliName.toLowerCase().includes("kandungan"))
            return "Dokter Kandungan";
        return `Spesialis ${poliName}`;
    };

    const helpers = { getImageUrl, getSpecialization, getPoliName };

    if (isTenant && tenantError) {
        return (
            <CareLineLayout slug={slug} activePage="dokter" logo={null} primaryColor="#06B6D4">
                <div className="flex flex-1 min-h-[50vh] flex-col items-center justify-center px-6 text-center font-jakarta">
                    <p className="text-slate-600 mb-4">Klinik tidak ditemukan atau tidak dapat dimuat.</p>
                    <Link
                        to={`/${slug}`}
                        className="text-sm font-semibold text-[#06B6D4] hover:underline"
                    >
                        Kembali ke beranda klinik
                    </Link>
                </div>
            </CareLineLayout>
        );
    }

    return (
        <CareLineLayout
            activePage="dokter"
            slug={isTenant ? slug : null}
            logo={clinicBrand?.logo_url}
            primaryColor={clinicBrand?.primary_color}
        >
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');
                @keyframes shimmer {
                    0%   { background-position: -600px 0; }
                    100% { background-position:  600px 0; }
                }
                @keyframes cardIn {
                    from { opacity: 0; transform: translateY(20px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes heroFadeUp {
                    from { opacity: 0; transform: translateY(28px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .hero-title { animation: heroFadeUp 0.7s cubic-bezier(0.4,0,0.2,1) 0.1s both; }
                .hero-sub   { animation: heroFadeUp 0.7s cubic-bezier(0.4,0,0.2,1) 0.3s both; }
            `}</style>

            {/* ── Hero ── */}
            <section
                className="relative flex flex-col items-center justify-center overflow-hidden"
                style={{
                    height: "clamp(280px, 42vw, 460px)",
                    backgroundImage: "url('/careline/doctors-hero.svg')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/25 to-slate-900/55" />
                <div className="relative z-10 text-center px-6">
                    <h1
                        className="hero-title font-[Plus_Jakarta_Sans] font-extrabold text-white leading-[1.1] tracking-[-0.01em]"
                        style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)" }}
                    >
                        Tim Dokter{" "}
                        <em className="not-italic text-transparent bg-clip-text bg-gradient-to-r from-[#a5f3fc] via-[#67e8f9] to-[#22d3ee]">
                            Profesional
                        </em>{" "}
                        Kami
                    </h1>
                    <p
                        className="hero-sub font-[DM_Sans] font-light text-white/75 max-w-[480px] mx-auto mt-4"
                        style={{ fontSize: "clamp(0.875rem, 1.5vw, 1rem)" }}
                    >
                        Didukung tenaga medis berpengalaman dan berlisensi untuk
                        memberikan pelayanan terbaik.
                    </p>
                </div>
                <div className="absolute bottom-[-1px] left-0 w-full z-10 leading-none">
                    <svg
                        viewBox="0 0 1440 56"
                        preserveAspectRatio="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="block w-full h-14"
                    >
                        <path
                            d="M0,32 C360,56 1080,0 1440,32 L1440,56 L0,56 Z"
                            fill="#f8fafc"
                        />
                    </svg>
                </div>
            </section>

            {/* ── Content ── */}
            <main
                className="relative bg-slate-50"
                style={{
                    backgroundImage:
                        "url('/patterns/careline-polyclinics-pattern.svg')",
                    backgroundSize: "auto",
                    backgroundPosition: "top center",
                }}
            >
                {/* ── Dokter Tersedia Hari Ini ── */}
                <section
                    className="relative z-10"
                    style={{
                        padding:
                            "clamp(48px, 8vw, 96px) 0 clamp(48px, 6vw, 80px)",
                    }}
                >
                    <div className="text-center px-4 mb-12 md:mb-16">
                        <h2
                            className="font-[Plus_Jakarta_Sans] font-bold text-slate-900 leading-[1.15] tracking-[-0.02em]"
                            style={{ fontSize: "clamp(1.5rem, 3vw, 2.25rem)" }}
                        >
                            Dokter Tersedia{" "}
                            <em className="not-italic text-transparent bg-clip-text bg-gradient-to-r from-[#06B6D4] to-[#0891B2]">
                                Hari Ini
                            </em>
                        </h2>
                        <p className="font-[DM_Sans] text-[0.9375rem] text-slate-500 font-light max-w-[420px] mx-auto mt-3">
                            Dokter yang sedang bertugas dan melayani pasien hari
                            ini.
                        </p>
                    </div>

                    <div
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mx-auto"
                        style={{
                            gap: "24px",
                            maxWidth: "1200px",
                            padding: "0 clamp(16px, 4vw, 48px)",
                        }}
                    >
                        {isLoading ? (
                            Array.from({ length: 3 }).map((_, i) => (
                                <SkeletonFeatured key={i} />
                            ))
                        ) : featuredDoctors.length === 0 ? (
                            <div className="col-span-1 sm:col-span-2 lg:col-span-3 text-center py-20 px-6">
                                <p className="font-[Plus_Jakarta_Sans] text-xl font-semibold text-slate-700 mb-2">
                                    Belum ada dokter tersedia
                                </p>
                                <p className="font-[DM_Sans] text-sm text-slate-400">
                                    Tidak ada dokter yang bertugas hari ini.
                                </p>
                            </div>
                        ) : (
                            featuredDoctors.map((doc, index) => (
                                <FeaturedCard
                                    key={doc.id_dokter}
                                    doc={doc}
                                    index={index}
                                    onSchedule={handleOpenSchedule}
                                    {...helpers}
                                />
                            ))
                        )}
                    </div>
                </section>

                {/* ── Daftar Dokter Klinik ── */}
                <section
                    className="relative z-10 border-t border-slate-100/60"
                    style={{
                        padding:
                            "clamp(48px, 6vw, 80px) 0 clamp(64px, 10vw, 120px)",
                    }}
                >
                    <div className="text-center px-4 mb-12 md:mb-16">
                        <h2
                            className="font-[Plus_Jakarta_Sans] font-bold text-slate-900 leading-[1.15] tracking-[-0.02em]"
                            style={{ fontSize: "clamp(1.5rem, 3vw, 2.25rem)" }}
                        >
                            Daftar Dokter{" "}
                            <em className="not-italic text-transparent bg-clip-text bg-gradient-to-r from-[#06B6D4] to-[#0891B2]">
                                Klinik
                            </em>
                        </h2>
                        <p className="font-[DM_Sans] text-[0.9375rem] text-slate-500 font-light max-w-[420px] mx-auto mt-3">
                            Informasi dokter yang terdaftar dan melayani di
                            klinik ini.
                        </p>
                    </div>

                    <div
                        className="grid grid-cols-1 md:grid-cols-2 mx-auto"
                        style={{
                            gap: "20px",
                            maxWidth: "1000px",
                            padding: "0 clamp(16px, 4vw, 48px)",
                        }}
                    >
                        {isLoading ? (
                            Array.from({ length: 4 }).map((_, i) => (
                                <SkeletonRow key={i} />
                            ))
                        ) : allDoctors.length === 0 ? (
                            <div className="col-span-1 md:col-span-2 text-center py-20 px-6">
                                <p className="font-[Plus_Jakarta_Sans] text-xl font-semibold text-slate-700 mb-2">
                                    Belum ada dokter terdaftar
                                </p>
                                <p className="font-[DM_Sans] text-sm text-slate-400">
                                    Tidak ada dokter yang terdaftar saat ini.
                                </p>
                            </div>
                        ) : (
                            allDoctors.map((doc, index) => (
                                <DoctorRow
                                    key={doc.id_dokter}
                                    doc={doc}
                                    index={index}
                                    onSchedule={handleOpenSchedule}
                                    {...helpers}
                                />
                            ))
                        )}
                    </div>
                </section>
            </main>

            <DoctorScheduleModal
                isOpen={isScheduleModalOpen}
                onClose={handleCloseSchedule}
                doctor={selectedDoctor}
            />
        </CareLineLayout>
    );
};

export default Doctors;